import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Badge, Button, Card, Dropdown, Form, Spinner } from 'react-bootstrap';
import { ChevronDown, ChevronRight } from 'react-bootstrap-icons';
import { FailureLine } from '@/components/deployments/FailureLine';
import { LiveRunCard } from '@/components/deployments/LiveRunCard';
import { RunActionDialog, type RunAction } from '@/components/deployments/RunActionDialog';
import { RunDetails } from '@/components/deployments/RunDetails';
import { RunMoreMenu } from '@/components/deployments/RunMoreMenu';
import { StatusPill } from '@/components/deployments/StatusPill';
import { PageHeader } from '@/components/ui/PageHeader';
import { useDeployClock } from '@/hooks/useDeployClock';
import { useDeployFailures } from '@/hooks/useDeployFailures';
import { useDeployImages } from '@/hooks/useDeployImages';
import { useDeployPlans } from '@/hooks/useDeployPlans';
import { useDeployProgress } from '@/hooks/useDeployProgress';
import { getDeploymentById, getLatestDeploymentForClient, type DeploymentRecord } from '@/services/deploymentService';
import {
  LOG_CHUNK_LINES,
  isMissingStreamError,
  loadLogEarlier,
  loadLogTail,
  resolveDeploymentLogTarget,
  stripAnsi,
  tailLogStream,
  taskIdFromArn,
} from '@/services/logsService';
import { describePlanOutcome, formatCount } from '@/utils/deployProgress';
import {
  imageDisplay,
  isFailed,
  isFinishedPlan,
  isLive,
  isLogExpired,
  shortUser,
  statusKey,
} from '@/utils/deployStatus';
import { formatDuration, formatNzDateTime, timeAgo } from '@/utils/relativeTime';
import '@/components/deployments/deployments.css';

type LineKind = 'error' | 'apply' | 'plan' | 'preplan' | '';

interface LogLine {
  text: string;
  kind: LineKind;
}

// Terraform prints an error inside a box, so the line starts with a bar and often the stack's name:
// "numa-acme  │ Error: …". The old check looked for "Error:" at the very start and missed every one of these.
const RE_ERROR = /^\s*(?:numa-\S+\s+)?[│╷╵]?\s*Error:/;
const RE_ERROR_TAG = /^\s*(?:\[\d{4}-\d{2}-\d{2}T[^\]]+\]\s*)?\[ERROR\]|^\s*ERROR\b|Invoking Terraform CLI failed/;
const RE_APPLY = /Apply complete!/;
const RE_PLAN = /^\s*(?:numa-\S+\s+)?Plan:|^\s*(?:numa-\S+\s+)?No changes\b/;
const RE_PREPLAN = /Terraform will perform the following actions:/;

function classify(text: string): LineKind {
  if (RE_ERROR.test(text) || RE_ERROR_TAG.test(text)) return 'error';
  if (RE_APPLY.test(text)) return 'apply';
  if (RE_PLAN.test(text)) return 'plan';
  if (RE_PREPLAN.test(text)) return 'preplan';
  return '';
}

/** Log events as lines to show. An event that holds only cursor codes is an empty line, and is dropped. */
function toLines(events: { message?: string }[]): LogLine[] {
  const lines: LogLine[] = [];
  for (const event of events) {
    const text = stripAnsi(event.message || '').replace(/\s+$/, '');
    if (text) lines.push({ text, kind: classify(text) });
  }
  return lines;
}

/** The most lines "Load the whole log" will put on the page. The biggest real log is about 110,000 lines. */
const MAX_LINES = 150_000;
const MARKER_LIST_LIMIT = 200;

const LogLines = memo(function LogLines({ lines }: { lines: LogLine[] }) {
  return (
    <pre className="deploy-log__lines">
      {lines.map((line, index) => (
        <div
          key={index}
          id={`deploy-log-line-${index}`}
          className={line.kind ? `deploy-log__line deploy-log__line--${line.kind}` : 'deploy-log__line'}
        >
          {line.text}
        </div>
      ))}
    </pre>
  );
});

/** A finished run's duration never changes. A live one counts up. */
function Duration({ record }: { record: DeploymentRecord }) {
  const live = isLive(record);
  const now = useDeployClock(live);
  return <span className="numeric">{formatDuration(record.startedAt, record.endedAt, now)}</span>;
}

export default function DeploymentLogs() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [deployment, setDeployment] = useState<DeploymentRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lines, setLines] = useState<LogLine[]>([]);
  const [logState, setLogState] = useState<'idle' | 'loading' | 'ready' | 'missing'>('idle');
  const [backwardToken, setBackwardToken] = useState<string | undefined>();
  const [reachedStart, setReachedStart] = useState(false);
  const [loadingEarlier, setLoadingEarlier] = useState<'chunk' | 'all' | null>(null);
  const [tailing, setTailing] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [paused, setPaused] = useState(false);
  const [held, setHeld] = useState(0);
  const [showDetails, setShowDetails] = useState(false);
  const [copied, setCopied] = useState(false);
  const [action, setAction] = useState<RunAction | null>(null);
  const [actionNewest, setActionNewest] = useState<DeploymentRecord | undefined>();

  const viewer = useRef<HTMLDivElement>(null);
  const tail = useRef<ReturnType<typeof tailLogStream> | null>(null);
  const pausedRef = useRef(false);
  const heldLines = useRef<LogLine[]>([]);
  const stopLoadingAll = useRef(false);
  // Where the view was before earlier lines were put above it, so it can be put back in the same place.
  const anchor = useRef<{ height: number; top: number } | null>(null);

  // `?task=<id>` shows the first attempt's log of a run the pipeline ran twice.
  const taskParam = searchParams.get('task') || undefined;
  const earlierTask = useMemo(() => {
    if (!deployment || !taskParam) return undefined;
    const current = taskIdFromArn(deployment.ecsTaskArn);
    return taskParam !== current ? taskParam : undefined;
  }, [deployment, taskParam]);

  const target = useMemo(
    () => (deployment ? resolveDeploymentLogTarget(deployment, earlierTask) : null),
    [deployment, earlierTask]
  );
  const live = deployment ? isLive(deployment) : false;
  const liveRef = useRef(live);
  liveRef.current = live;

  // ── The run's record ─────────────────────────────────────────────────────
  const loadRecord = useCallback(
    async (consistent: boolean) => {
      if (!id) return null;
      const record = await getDeploymentById(id, consistent);
      // Keep the same object when nothing changed, so the log is not read again.
      setDeployment((prev) => (prev && record && JSON.stringify(prev) === JSON.stringify(record) ? prev : record));
      return record;
    },
    [id]
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadRecord(true)
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load this deployment.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loadRecord]);

  // A live run's record is read every five seconds: its status, and the task once it has one.
  useEffect(() => {
    if (!live) return;
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') loadRecord(false).catch(() => undefined);
    }, 5000);
    return () => clearInterval(timer);
  }, [live, loadRecord]);

  // ── The log ──────────────────────────────────────────────────────────────
  const group = target?.group;
  const stream = target?.stream;

  useEffect(() => {
    if (!group || !stream) return;
    let cancelled = false;
    setLines([]);
    setLogState('loading');
    setReachedStart(false);
    setBackwardToken(undefined);
    heldLines.current = [];
    setHeld(0);

    // With a token the tail carries on exactly after the lines already shown. With none there is
    // no log yet, so it reads from the first line when one appears.
    const startTail = (forwardToken?: string, lastTs?: number) => {
      const follow = tailLogStream({
        group,
        stream,
        startAtEnd: Boolean(forwardToken),
        intervalMs: 1500,
        initialForwardToken: forwardToken,
        initialLastTs: lastTs,
        onEvents: (events) => {
          const fresh = toLines(events);
          if (fresh.length === 0) return;
          if (pausedRef.current) {
            // Paused: hold the new lines, do not lose them.
            heldLines.current.push(...fresh);
            setHeld(heldLines.current.length);
            return;
          }
          setLines((prev) => [...prev, ...fresh]);
        },
        onError: (e) => setError(e instanceof Error ? e.message : 'The live log stopped.'),
      });
      tail.current = follow;
      follow.start();
      setTailing(true);
    };

    (async () => {
      try {
        // The END of the log first. A deploy log is about 25,000 lines, and the apply and any
        // error are at the end: reading from the start used to stop at 20,000 and never reach them.
        const chunk = await loadLogTail(group, stream);
        if (cancelled) return;
        setLines(toLines(chunk.events));
        setBackwardToken(chunk.backwardToken);
        setReachedStart(chunk.reachedStart);
        setLogState('ready');
        if (liveRef.current && !earlierTask) {
          startTail(chunk.forwardToken, chunk.events[chunk.events.length - 1]?.timestamp);
        }
      } catch (e) {
        if (cancelled) return;
        if (isMissingStreamError(e)) {
          // No stream yet: a live run's task has not written a line. Follow it so the log appears when it does.
          setLogState(liveRef.current && !earlierTask ? 'ready' : 'missing');
          setReachedStart(true);
          if (liveRef.current && !earlierTask) startTail();
        } else {
          setLogState('ready');
          setError(e instanceof Error ? e.message : 'Could not read the log.');
        }
      }
    })();

    return () => {
      cancelled = true;
      tail.current?.stop();
      tail.current = null;
      setTailing(false);
    };
  }, [group, stream, earlierTask]);

  // The run ended. Follow the log a few seconds more for its last lines, then stop.
  useEffect(() => {
    if (live || !tailing) return;
    const timer = setTimeout(() => {
      tail.current?.stop();
      tail.current = null;
      setTailing(false);
    }, 6000);
    return () => clearTimeout(timer);
  }, [live, tailing]);

  useEffect(() => {
    pausedRef.current = paused;
    if (!paused && heldLines.current.length > 0) {
      const release = heldLines.current;
      heldLines.current = [];
      setHeld(0);
      setLines((prev) => [...prev, ...release]);
    }
  }, [paused]);

  // Keep the view where it was after earlier lines go in above it. Otherwise follow the end.
  useLayoutEffect(() => {
    const el = viewer.current;
    if (!el) return;
    if (anchor.current) {
      el.scrollTop = el.scrollHeight - anchor.current.height + anchor.current.top;
      anchor.current = null;
    } else if (autoScroll) {
      el.scrollTop = el.scrollHeight;
    }
  }, [lines, autoScroll]);

  // Scrolling away from the end stops the follow, so a live log does not pull the view back
  // while somebody reads. Scrolling back to the end starts it again.
  const handleScroll = () => {
    const el = viewer.current;
    if (!el || anchor.current) return;
    const atEnd = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    setAutoScroll((current) => (current === atEnd ? current : atEnd));
  };

  const readEarlier = useCallback(
    async (token: string) => {
      if (!group || !stream) return null;
      const chunk = await loadLogEarlier(group, stream, token);
      const el = viewer.current;
      if (el) anchor.current = { height: el.scrollHeight, top: el.scrollTop };
      setLines((prev) => [...toLines(chunk.events), ...prev]);
      setBackwardToken(chunk.backwardToken);
      setReachedStart(chunk.reachedStart);
      return chunk;
    },
    [group, stream]
  );

  const loadEarlier = async () => {
    if (!backwardToken || loadingEarlier) return;
    setLoadingEarlier('chunk');
    try {
      await readEarlier(backwardToken);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read the earlier lines.');
    } finally {
      setLoadingEarlier(null);
    }
  };

  const loadWholeLog = async () => {
    if (!backwardToken || loadingEarlier) return;
    setLoadingEarlier('all');
    stopLoadingAll.current = false;
    try {
      let token: string | undefined = backwardToken;
      let loaded = lines.length;
      while (token && !stopLoadingAll.current && loaded < MAX_LINES) {
        const chunk = await readEarlier(token);
        if (!chunk || chunk.reachedStart) break;
        loaded += chunk.events.length;
        token = chunk.backwardToken;
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read the earlier lines.');
    } finally {
      setLoadingEarlier(null);
    }
  };

  // ── Marks in the log ─────────────────────────────────────────────────────
  const markers = useMemo(() => {
    const found: Record<Exclude<LineKind, ''>, number[]> = { error: [], apply: [], plan: [], preplan: [] };
    lines.forEach((line, index) => {
      if (line.kind) found[line.kind].push(index);
    });
    return found;
  }, [lines]);

  const jumpTo = useCallback((index: number) => {
    setAutoScroll(false);
    const el = document.getElementById(`deploy-log-line-${index}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('deploy-log__line--selected');
    setTimeout(() => el.classList.remove('deploy-log__line--selected'), 4000);
  }, []);

  // ── Why it failed, and live progress ─────────────────────────────────────
  const failed = deployment ? isFailed(deployment) : false;
  const needsReason = deployment && (failed || statusKey(deployment) === 'resolved') ? [deployment] : [];
  const failures = useDeployFailures(
    earlierTask ? [] : needsReason,
    deployment ? { [deployment.deploymentId]: showDetails } : undefined
  );
  const failure = deployment ? failures[deployment.deploymentId] : undefined;
  const progress = useDeployProgress(deployment && live && !earlierTask ? [deployment] : []);
  // The image by its given name, once the names arrive. Its tag until then.
  const imageNames = useDeployImages(deployment ? (deployment.repository ?? 'numa-deploy') : undefined);
  // A plan-only run's status cannot say what the plan found. That is read from the log.
  const plans = useDeployPlans(deployment && isFinishedPlan(deployment) && !earlierTask ? [deployment] : []);
  const plan = deployment ? plans[deployment.deploymentId] : undefined;
  const planText = describePlanOutcome(plan?.outcome);

  // A finished run may have been run again since. Find the client's newest run once, so this page does
  // not offer a second run for a failure a later run already replaced, and can warn before an older
  // image goes back on the client.
  const [latestRun, setLatestRun] = useState<{ checked: boolean; newer?: DeploymentRecord }>({ checked: false });
  const finishedId = deployment && !live ? deployment.deploymentId : undefined;
  useEffect(() => {
    setLatestRun({ checked: false });
    if (!finishedId || !deployment) return;
    let cancelled = false;
    getLatestDeploymentForClient(deployment.clientName)
      .then((newest) => {
        if (cancelled) return;
        const newer =
          newest && newest.deploymentId !== finishedId && (newest.startedAt || '') > (deployment.startedAt || '');
        setLatestRun({ checked: true, newer: newer ? newest : undefined });
      })
      .catch((e) => {
        console.warn('Could not check for a newer run', e);
        if (!cancelled) setLatestRun({ checked: true });
      });
    return () => {
      cancelled = true;
    };
  }, [finishedId]);

  const openAction = (next: RunAction) => {
    setAction(next);
    // "Run again" can put an older image back on the client: the dialog warns when a newer run exists.
    setActionNewest(next.type === 'run-again' ? latestRun.newer : undefined);
  };

  const handlers = {
    onRunAgain: (record: DeploymentRecord) => openAction({ type: 'run-again', record }),
    onStop: (record: DeploymentRecord) => openAction({ type: 'stop', record }),
    onMarkFailed: (record: DeploymentRecord) => openAction({ type: 'mark-failed', record }),
    onMarkResolved: (record: DeploymentRecord) => openAction({ type: 'mark-resolved', record }),
  };

  const copyLog = async () => {
    try {
      await navigator.clipboard.writeText(lines.map((line) => line.text).join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      console.error('Copy failed', e);
    }
  };

  const downloadLog = () => {
    const blob = new Blob([lines.map((line) => line.text).join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${deployment?.clientName || 'deploy'}-${id || 'log'}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const image = deployment ? imageDisplay(deployment, imageNames) : null;
  // A member of a group run goes back to its group. Any other run goes back to the list.
  const backTo = deployment?.groupRunId
    ? { to: `/deployments/group/${encodeURIComponent(deployment.groupRunId)}`, label: 'Group run' }
    : { to: '/deployments', label: 'Deployments' };
  const firstTask = deployment?.firstAttemptTaskArn;
  const firstError = markers.error[0];
  const markerGroups: { key: Exclude<LineKind, ''>; label: string }[] = [
    { key: 'error', label: 'Errors' },
    { key: 'preplan', label: 'Start of the plan' },
    { key: 'plan', label: 'Plan result' },
    { key: 'apply', label: 'Apply complete' },
  ];
  const hasMarkers = markerGroups.some((g) => markers[g.key].length > 0);

  let extent = '';
  if (logState === 'ready' && lines.length > 0) {
    extent = reachedStart
      ? `The whole log: ${formatCount(lines.length)} lines`
      : `The last ${formatCount(lines.length)} lines of the log`;
  }

  return (
    <div className="deploy-page">
      <PageHeader
        title="Deployment log"
        // Which run this is: the client, the image, when it started.
        description={
          deployment && image
            ? `${deployment.clientName} · ${image.label} · started ${timeAgo(deployment.startedAt)}`
            : undefined
        }
        back={backTo}
        actions={deployment && !live ? <RunMoreMenu record={deployment} large {...handlers} /> : undefined}
      />

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {loading && (
        <Card className="deploy-summary">
          <div className="deploy-empty">
            <Spinner animation="border" size="sm" className="me-2" />
            Loading the deployment
          </div>
        </Card>
      )}
      {!loading && !deployment && !error && (
        <Alert variant="warning">No deployment with this ID was found. Open one from the Deployments page.</Alert>
      )}

      {deployment && earlierTask && (
        <Alert variant="info">
          This is the log of the first attempt. The pipeline ran this deploy a second time.{' '}
          <Link to={`/deployments/${encodeURIComponent(deployment.deploymentId)}/logs`}>
            Open the second attempt's log
          </Link>
        </Alert>
      )}

      {/* A live run gets the same status line as on the Deployments page. */}
      {deployment && live && !earlierTask && (
        <section className="deploy-section">
          <LiveRunCard record={deployment} snapshot={progress[deployment.deploymentId]} hideLogsLink {...handlers} />
        </section>
      )}

      {deployment && !live && (
        <Card className="deploy-summary">
          <div className="deploy-summary__head">
            <span className="deploy-live-card__client">{deployment.clientName}</span>
            <StatusPill status={deployment.status} attemptNumber={deployment.attemptNumber} />
            <div className="deploy-live-card__meta deploy-summary__meta">
              <span className={image?.named ? undefined : 'mono'} title={image?.tag}>
                {image?.label}
              </span>
              {image?.dev && <Badge bg="warning">Dev</Badge>}
              {deployment.deploymentLabel && <span>{deployment.deploymentLabel}</span>}
              <span title={deployment.initiatedBy}>by {shortUser(deployment.initiatedBy) || 'unknown'}</span>
              <span title={formatNzDateTime(deployment.startedAt)}>started {timeAgo(deployment.startedAt)}</span>
              <span>
                took <Duration record={deployment} />
              </span>
            </div>
            <div className="deploy-summary__tools">
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => setShowDetails((value) => !value)}
                aria-expanded={showDetails}
              >
                {showDetails ? <ChevronDown size={12} className="me-1" /> : <ChevronRight size={12} className="me-1" />}
                Details
              </Button>
            </div>
          </div>
          {failed && !earlierTask && (
            <div className="deploy-summary__reason">
              <FailureLine
                info={failure}
                newerRun={latestRun.newer}
                hideOffer={!latestRun.checked}
                onRunAgain={() => handlers.onRunAgain(deployment)}
              />
            </div>
          )}
          {firstTask && firstTask !== deployment.ecsTaskArn && !earlierTask && (
            <p className="deploy-note">
              The pipeline ran this deploy twice. This is the second attempt.{' '}
              <Link
                to={`/deployments/${encodeURIComponent(deployment.deploymentId)}/logs?task=${encodeURIComponent(taskIdFromArn(firstTask) || '')}`}
              >
                Open the first attempt's log
              </Link>
            </p>
          )}
          {planText && (
            <div className="deploy-summary__reason">
              <div className="deploy-reason">
                <span className="deploy-reason__title">Plan result</span>
                <span className="deploy-reason__text" title={`${planText}. A plan changes nothing.`}>
                  {planText}
                </span>
              </div>
            </div>
          )}
          {showDetails && <RunDetails record={deployment} failure={failure} plan={plan} />}
        </Card>
      )}

      {deployment && (
        <Card className="deploy-log">
          <div className="deploy-log__toolbar">
            <div className="deploy-log__extent">
              {tailing && (
                <Badge bg="info" className="me-2">
                  <span className="deploy-status__dot" aria-hidden="true" />
                  Live
                </Badge>
              )}
              {extent}
              {paused && held > 0 && ` · ${formatCount(held)} new lines held while paused`}
            </div>
            <div className="deploy-log__tools">
              {firstError !== undefined && (
                <Button size="sm" variant="outline-danger" onClick={() => jumpTo(firstError)}>
                  Jump to error
                </Button>
              )}
              {hasMarkers && (
                <Dropdown align="end">
                  <Dropdown.Toggle size="sm" variant="outline-secondary" id="deploy-log-jump">
                    Jump to
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="deploy-log__menu">
                    {markerGroups
                      .filter((g) => markers[g.key].length > 0)
                      .map((g) => (
                        <div key={g.key}>
                          <Dropdown.Header>
                            {g.label} ({markers[g.key].length})
                          </Dropdown.Header>
                          {markers[g.key].slice(0, MARKER_LIST_LIMIT).map((index) => (
                            <Dropdown.Item as="button" key={index} onClick={() => jumpTo(index)}>
                              <span className="deploy-log__menu-line">{lines[index]?.text.trim().slice(0, 90)}</span>
                            </Dropdown.Item>
                          ))}
                        </div>
                      ))}
                  </Dropdown.Menu>
                </Dropdown>
              )}
              <Form.Check
                type="switch"
                id="deploy-log-follow"
                label="Follow the end"
                checked={autoScroll}
                onChange={(e) => setAutoScroll(e.currentTarget.checked)}
              />
              {tailing && (
                <Button size="sm" variant="outline-secondary" onClick={() => setPaused((value) => !value)}>
                  {paused ? 'Resume' : 'Pause'}
                </Button>
              )}
              <Button size="sm" variant="outline-secondary" onClick={copyLog} disabled={lines.length === 0}>
                {copied ? 'Copied' : 'Copy'}
              </Button>
              <Button size="sm" variant="outline-secondary" onClick={downloadLog} disabled={lines.length === 0}>
                Download
              </Button>
              <Dropdown align="end">
                <Dropdown.Toggle size="sm" variant="outline-secondary" id="deploy-log-more">
                  More
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item as="button" onClick={loadWholeLog} disabled={reachedStart || Boolean(loadingEarlier)}>
                    Load the whole log
                  </Dropdown.Item>
                  <Dropdown.Item as="button" onClick={() => setLines([])} disabled={lines.length === 0}>
                    Clear the view
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>

          <div className="deploy-log__viewer" ref={viewer} onScroll={handleScroll}>
            {!stream && (
              <div className="deploy-log__state">
                {live
                  ? 'The deploy task has not started yet. Its log appears here when it does.'
                  : 'This run never started a deploy task, so it has no log.'}
              </div>
            )}
            {stream && logState === 'loading' && (
              <div className="deploy-log__state">
                <Spinner animation="border" size="sm" className="me-2" />
                Loading the end of the log
              </div>
            )}
            {stream && logState === 'missing' && (
              <div className="deploy-log__state">
                {isLogExpired(deployment)
                  ? 'The log for this run has expired. Deploy logs are kept for 30 days.'
                  : 'This run wrote no log. The deploy task did not start.'}
              </div>
            )}
            {stream && logState === 'ready' && lines.length === 0 && !loadingEarlier && (
              <div className="deploy-log__state">
                {tailing
                  ? 'Waiting for the first line. The deploy task takes about two minutes to start.'
                  : isLogExpired(deployment)
                    ? 'The log for this run has expired. Deploy logs are kept for 30 days.'
                    : 'Nothing to show.'}
              </div>
            )}
            {stream && logState === 'ready' && lines.length > 0 && !reachedStart && (
              <div className="deploy-log__earlier">
                {loadingEarlier === 'all' ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading the whole log: {formatCount(lines.length)} lines so far
                    <button type="button" onClick={() => (stopLoadingAll.current = true)}>
                      Stop
                    </button>
                  </>
                ) : (
                  <>
                    <button type="button" onClick={loadEarlier} disabled={Boolean(loadingEarlier)}>
                      {loadingEarlier === 'chunk' ? 'Loading' : `Load ${formatCount(LOG_CHUNK_LINES)} earlier lines`}
                    </button>
                    <button type="button" onClick={loadWholeLog} disabled={Boolean(loadingEarlier)}>
                      Load the whole log
                    </button>
                  </>
                )}
              </div>
            )}
            <LogLines lines={lines} />
          </div>
          {stream && (
            <div className="deploy-log__foot">
              <span className="mono">{stream}</span>
            </div>
          )}
        </Card>
      )}

      <RunActionDialog
        action={action}
        newerRun={actionNewest}
        failure={failure}
        onClose={() => setAction(null)}
        onDone={(done, result) => {
          setAction(null);
          // A second run is a new deployment with its own log. Go to it.
          if (done.type === 'run-again' && result.deploymentId) {
            navigate(`/deployments/${encodeURIComponent(result.deploymentId)}/logs`);
          } else {
            loadRecord(true).catch(() => undefined);
          }
        }}
      />
    </div>
  );
}
